import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const API_RESPONSE = {
  medallion_pool_id: "pool_0x7a3f...",
  query_result: {
    cohort_size: 50000,
    demographic_distribution: "...",
    behavioral_signal: "high_intent_purchase_category",
    confidence_interval: 0.95,
    differential_privacy_epsilon: 0.1
  },
  provenance: {
    data_quality_score: 0.94,
    verification_method: "doppelganger_sandbox",
    user_consent_binding: "ed25519_signed",
    settlement_tx: "hammurabi_handoff_0x9c2e..."
  },
  raw_data_access: false,
  re_identification_risk: "null"
};

export default function QuerySpec() {
  const [expanded, setExpanded] = useState(null);

  const sections = [
    { key: 'query_result', label: 'Query Result', color: 'cyan' },
    { key: 'provenance', label: 'Provenance', color: 'amber' },
    { key: 'access', label: 'Access Control', color: 'fuchsia' },
  ];

  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden">
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-zinc-800 bg-zinc-900/50">
        <div className="w-2 h-2 rounded-full bg-rose-500/60" />
        <div className="w-2 h-2 rounded-full bg-amber-500/60" />
        <div className="w-2 h-2 rounded-full bg-emerald-500/60" />
        <span className="ml-2 text-[10px] text-zinc-500 tracking-widest uppercase">corporate_query_response.json</span>
      </div>

      <div className="p-5 font-mono text-xs">
        <div className="text-zinc-600 mb-2">{"// What a corporate buyer receives"}</div>
        <div className="text-zinc-500">{"{"}</div>
        
        <div className="ml-4 mb-2">
          <span className="text-cyan-400">"medallion_pool_id"</span>
          <span className="text-zinc-600">: </span>
          <span className="text-amber-400">"pool_0x7a3f..."</span>
          <span className="text-zinc-600">,</span>
        </div>

        {sections.map((section) => (
          <div key={section.key} className="mb-2">
            <button
              onClick={() => setExpanded(expanded === section.key ? null : section.key)}
              className="w-full text-left flex items-center gap-2 hover:bg-zinc-900/50 p-1 -mx-1 rounded transition-colors"
            >
              <SafeIcon 
                icon={expanded === section.key ? FiIcons.FiChevronDown : FiIcons.FiChevronRight} 
                className="text-zinc-600 text-xs" 
              />
              <span className={`text-${section.color}-400`}>
                {section.key === 'access' ? '"access_control"' : `"${section.key}"`}
              </span>
              <span className="text-zinc-600">: </span>
              <span className="text-zinc-500">{"{...}"}</span>
              <span className="text-zinc-600">,</span>
            </button>

            <AnimatePresence>
              {expanded === section.key && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="ml-8 overflow-hidden"
                >
                  {section.key === 'query_result' && (
                    <div className="border-l-2 border-cyan-500/30 pl-3 py-2 space-y-1">
                      <div><span className="text-zinc-500">"cohort_size"</span>: <span className="text-emerald-400">50000</span>,</div>
                      <div><span className="text-zinc-500">"behavioral_signal"</span>: <span className="text-amber-400">"high_intent_purchase_category"</span>,</div>
                      <div><span className="text-zinc-500">"confidence_interval"</span>: <span className="text-emerald-400">0.95</span>,</div>
                      <div><span className="text-zinc-500">"differential_privacy_epsilon"</span>: <span className="text-emerald-400">0.1</span></div>
                    </div>
                  )}
                  {section.key === 'provenance' && (
                    <div className="border-l-2 border-amber-500/30 pl-3 py-2 space-y-1">
                      <div><span className="text-zinc-500">"data_quality_score"</span>: <span className="text-emerald-400">0.94</span>,</div>
                      <div><span className="text-zinc-500">"verification_method"</span>: <span className="text-amber-400">"doppelganger_sandbox"</span>,</div>
                      <div><span className="text-zinc-500">"user_consent_binding"</span>: <span className="text-amber-400">"ed25519_signed"</span>,</div>
                      <div><span className="text-zinc-500">"settlement_tx"</span>: <span className="text-amber-400">"hammurabi_handoff_0x9c2e..."</span></div>
                    </div>
                  )}
                  {section.key === 'access' && (
                    <div className="border-l-2 border-fuchsia-500/30 pl-3 py-2 space-y-1">
                      <div><span className="text-zinc-500">"raw_data_access"</span>: <span className="text-rose-400">false</span>, <span className="text-zinc-700">{"// Never true"}</span></div>
                      <div><span className="text-zinc-500">"re_identification_risk"</span>: <span className="text-rose-400">"null"</span></div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}

        <div className="text-zinc-500">{"}"}</div>
      </div>
    </div>
  );
}